import { useState } from 'react';
import clsx from 'clsx';
import { OrderConflictError, updateOrderFields, type OrderDetail, type OrderFieldMutation } from '../../lib/orders';

type FlagKey =
  | 'spoed'
  | 'zelfBrengen'
  | 'zelfHalen'
  | 'geenOpslagkosten'
  | 'geenMinimumTariefWederverkoper'
  | 'bevestigingenSturen'
  | 'ophalenHandmatigInplannen'
  | 'afleverenHandmatigInplannen';

interface FlagDefinition {
  key: FlagKey;
  label: string;
  /** Tooltip-tekst — verschijnt op hover als title-attr. */
  description: string;
  /** Tailwind classes voor de actieve staat. */
  activeClass: string;
  /** Wanneer true is de "normale" staat AAN — UIT toont een waarschuwings-pill ipv grijs. */
  warnWhenOff?: boolean;
  /** Label dat getoond wordt wanneer de pill UIT staat (alleen i.c.m. warnWhenOff). */
  offLabel?: string;
}

const FLAG_DEFS: FlagDefinition[] = [
  {
    key: 'spoed',
    label: 'Spoed',
    description: 'Behandel deze order met voorrang in de wasserij.',
    activeClass: 'bg-orange-100 text-orange-700',
  },
  {
    key: 'zelfBrengen',
    label: 'Zelf brengen',
    description: 'Klant brengt het kleed zelf naar Havelte.',
    activeClass: 'bg-indigo-100 text-indigo-700',
  },
  {
    key: 'zelfHalen',
    label: 'Zelf halen',
    description: 'Klant haalt het kleed zelf op na reiniging.',
    activeClass: 'bg-indigo-100 text-indigo-700',
  },
  {
    key: 'geenOpslagkosten',
    label: 'Geen opslagkosten',
    description: 'Reken geen opslagkosten voor deze order.',
    activeClass: 'bg-emerald-100 text-emerald-700',
  },
  {
    key: 'geenMinimumTariefWederverkoper',
    label: 'Geen min. tarief',
    description: 'Sla het minimum-tarief over bij wederverkoper-orders.',
    activeClass: 'bg-emerald-100 text-emerald-700',
  },
  {
    key: 'bevestigingenSturen',
    label: 'Bevestigingen',
    description:
      'Stuur automatische bevestigings- en plannings-e-mails. Zet UIT om mail-triggers te onderdrukken (bv. bij handmatige status-wissel).',
    activeClass: 'bg-slate-100 text-slate-700',
    warnWhenOff: true,
    offLabel: 'Geen bevestigingen',
  },
  {
    key: 'ophalenHandmatigInplannen',
    label: 'Ophalen handmatig',
    description:
      'Geen automatisch ophaalvoorstel — plan het ophalen met de hand (buiten routesysteem: buitenland, geen passende route, of buiten werkgebied).',
    activeClass: 'bg-purple-100 text-purple-700',
  },
  {
    key: 'afleverenHandmatigInplannen',
    label: 'Afleveren handmatig',
    description:
      'Geen automatisch aflevervoorstel — plan het afleveren met de hand (buiten routesysteem: buitenland, geen passende route, of buiten werkgebied).',
    activeClass: 'bg-purple-100 text-purple-700',
  },
];

interface OrderFlagsRowProps {
  order: OrderDetail;
  onSaved: (nieuweLastModifiedDate: string | null) => void;
}

export function OrderFlagsRow({ order, onSaved }: OrderFlagsRowProps) {
  const [pendingKey, setPendingKey] = useState<FlagKey | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [optimistic, setOptimistic] = useState<Partial<Record<FlagKey, boolean>>>({});

  const flags = order.flags;
  const showWederverkoper = order.flagsApplicable?.geenMinimumTariefWederverkoper === true;
  const visibleFlags = FLAG_DEFS.filter(
    (f) => f.key !== 'geenMinimumTariefWederverkoper' || showWederverkoper
  );

  if (!flags) return null;

  const currentValue = (key: FlagKey): boolean => {
    if (optimistic[key] !== undefined) return optimistic[key]!;
    return flags[key];
  };

  const toggle = async (key: FlagKey) => {
    if (pendingKey) return;
    const desired = !currentValue(key);
    setPendingKey(key);
    setError(null);
    setOptimistic((prev) => ({ ...prev, [key]: desired }));

    try {
      const mutation: OrderFieldMutation = { flags: { [key]: desired } };
      const result = await updateOrderFields(order.id, mutation, order.lastModifiedDate);
      onSaved(result.nieuweLastModifiedDate);
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      setOptimistic((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (err instanceof OrderConflictError) {
        setError('Order is gewijzigd. Vernieuwen…');
        window.dispatchEvent(new CustomEvent('order:conflict'));
      } else {
        setError(err instanceof Error ? err.message : 'Opslaan mislukt');
      }
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100">
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
        Opties
        <span className="ml-1.5 font-normal normal-case tracking-normal text-slate-400">
          — klik om aan of uit te zetten
        </span>
      </p>
      <div className="flex items-center gap-1.5 flex-wrap">
        {visibleFlags.map((def) => {
          const value = currentValue(def.key);
          const isPending = pendingKey === def.key;
          const isOtherPending = pendingKey !== null && pendingKey !== def.key;
          const showAsWarning = def.warnWhenOff === true && !value;
          const afgeleid = !value && order.flagsAfgeleid?.[def.key as 'geenMinimumTariefWederverkoper'] === true;
          const label = showAsWarning && def.offLabel ? def.offLabel : afgeleid ? `${def.label} (zelf halen/brengen)` : def.label;
          const title = afgeleid
            ? 'Geldt automatisch: wederverkoper die zelf brengt én zelf haalt. Aanzetten pint het vast.'
            : def.description;
          return (
            <button
              key={def.key}
              type="button"
              role="switch"
              aria-checked={value}
              aria-label={label}
              title={title}
              onClick={() => toggle(def.key)}
              disabled={isPending || isOtherPending}
              className={clsx(
                'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium transition-colors',
                showAsWarning
                  ? 'bg-amber-100 text-amber-800'
                  : value
                  ? def.activeClass
                  : afgeleid
                  ? 'border border-emerald-300 text-emerald-700'
                  : 'border border-slate-200 text-slate-400 hover:text-slate-700 hover:border-slate-300',
                (isPending || isOtherPending) && 'opacity-60 cursor-not-allowed',
              )}
            >
              {isPending ? (
                <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
                  <path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" />
                </svg>
              ) : (
                <span
                  className={clsx(
                    'inline-block w-1.5 h-1.5 rounded-full',
                    showAsWarning ? 'bg-current opacity-80' : value ? 'bg-current opacity-70' : 'bg-slate-300'
                  )}
                />
              )}
              <span>{label}</span>
            </button>
          );
        })}
      </div>
      {error && <p className="mt-1.5 text-[11px] text-amber-700">⚠ {error}</p>}
    </div>
  );
}
